import { IBody, IBodySettings, IOverlay, IPage } from '@makyoapp/makyo-sdk/models/AppBuilder'

import { IDB } from '@/core/db'

import { IAppProvider } from '../provider/provider'
import { UndoableOptions } from '../undo/types'
import { createCoalescingKey, generateUndoActionId, getComponentLabel } from '../undo/utils'
import { updateWithTimeout } from '../utils'
import { IAppClient } from './app'
import { IViewComponent, ViewComponent } from './component'

type AppLayerSubscriber = () => void

type ILayer = IPage | IOverlay

export interface IAppLayer {
  /**
   * AppClient instance
   */
  appClient: IAppClient
  /**
   * DbClient instance
   */
  dbClient: IDB
  /**
   * The provider for the app
   */
  provider: IAppProvider
  /**
   * The index of the layer in app-builder.
   */
  index: number
  /**
   * Layer settings
   */
  settings: ILayer
  /**
   * Type of the layer
   */
  type: 'page' | 'overlay'
  /**
   * App ID of the current project
   */
  appID: string
  /**
   * Components in the layer
   */
  componentsMap: Map<string, IViewComponent>
  /**
   * Subscriber
   */
  subscribers: Set<AppLayerSubscriber>
  /**
   * Subscribe to update
   */
  subscribe(callback: AppLayerSubscriber): () => void
  /**
   * Update the layer settings
   * @param layer - updated layer (optional), supports partial updates
   * @param rebuildComponents - Whether to rebuild the component map
   * @param options - Undo/redo options
   */
  Update(layer?: Partial<ILayer>, rebuildComponents?: boolean, options?: UndoableOptions): Promise<void>
  /**
   * Disable layer
   * @param value - The value to disable the layer
   */
  Disable(value: boolean): Promise<void>
  /**
   * Add component to the layer
   * @param component - The body of the component to add (can be nested)
   * @param flatSettings - The settings of all components -> `{ [componentId]: { [settingId]: value } }`
   * @param parentId - The id of the parent component (optional)
   * @param position - The position of the component within the parent (optional)
   * @param options - Undo/redo options
   */
  AddComponent(
    component: IBody,
    flatSettings: Record<string, any>,
    parentId?: string,
    position?: number,
    options?: UndoableOptions
  ): Promise<void>
  /**
   * Update component in the layer
   * @param component - The component to update
   * @param layer - updated layer (optional)
   * @param options - Undo/redo options
   */
  UpdateComponent(
    component: IViewComponent,
    bodyUpdate?: {
      bodyArray?: IBody[]
      bodySettings?: IBodySettings
    },
    options?: UndoableOptions
  ): Promise<void>
  /**
   * Update component settings in the layer
   * @param component - The component to update
   * @param options - Undo/redo options
   */
  UpdateComponentSettings(component: IViewComponent, options?: UndoableOptions): Promise<void>
  /**
   * Delete component from the layer
   * @param componentId - The id of the component to delete
   * @param options - Undo/redo options
   */
  DeleteComponent(componentId: string, options?: UndoableOptions): Promise<void>
}

/**
 * AppLayer is a base instance for page and overlay,
 * dedicated to handle abstraction for app builder(page/overlay) methods
 * such as component operations and settings updates.
 *
 * if we need to add a specific method for one of the types, we can do it AppPage or AppOverlay.
 */
export class AppLayer implements IAppLayer {
  appClient: IAppClient
  dbClient: IDB
  provider: IAppProvider
  appID: string
  index: number
  settings: ILayer
  componentsMap: Map<string, IViewComponent> = new Map()
  subscribers: Set<AppLayerSubscriber> = new Set()
  type: 'page' | 'overlay'

  constructor(appClient: IAppClient, index: number, layer: ILayer, type: 'page' | 'overlay') {
    this.appClient = appClient
    this.dbClient = appClient.dbClient!
    this.provider = appClient.provider
    this.appID = appClient.appID || ''
    this.index = index
    this.settings = layer
    this.type = type

    this.buildComponentsMap()
  }

  //--------------------------------------------------------------------------------------//
  //                             Component Map Methods                                    //
  //--------------------------------------------------------------------------------------//

  private buildComponentsMap() {
    this.componentsMap.clear()
    this.settings._Body.forEach((body) => {
      this.createComponent(body)
    })

    // Inject global header/footer components into the map so they persist across rebuilds// Prevents global from dissapearing when page is rebuilt
    const globals = ((this.appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any
    const globalsBody = (globals?._Body || []) as any[]
    const globalsSettings = (globals?._BodySettings || {}) as Record<string, any>

    const hasComponentDeep = (bodies: any[], targetId: string): boolean => {
      for (const b of bodies || []) {
        if (b?._ComponentID === targetId) return true
        if (Array.isArray(b?._Body) && b._Body.length > 0 && hasComponentDeep(b._Body, targetId)) return true
      }
      return false
    }

    const pageHasHeader = hasComponentDeep(this.settings._Body as any[], 'header')
    const pageHasFooter = hasComponentDeep(this.settings._Body as any[], 'footer')

    const globalSettings = ((this.settings as any)?._GlobalSettings || {}) as any
    const headerVisible = globalSettings._HideHeader !== true
    const footerVisible = globalSettings._HideFooter !== true

    const inject = (bodies: any[], parentId?: string) => {
      for (const body of bodies) {
        // copy settings (in-memory)
        this.settings._BodySettings = {
          ...(this.settings._BodySettings || {}),
          [body._id]: globalsSettings[body._id] || {},
        } as any
        // register component
        this.componentsMap.set(body._id, new ViewComponent(this, body, parentId))
        if (Array.isArray(body._Body) && body._Body.length > 0) {
          inject(body._Body, body._id)
        }
      }
    }

    // header/footer globals live under group 'globals'
    if (headerVisible && !pageHasHeader) {
      const headerBodies = globalsBody.filter((b) => b._ComponentID === 'header' && b._GroupID === 'globals')
      if (headerBodies.length) inject(headerBodies)
    }
    if (footerVisible && !pageHasFooter) {
      const footerBodies = globalsBody.filter((b) => b._ComponentID === 'footer' && b._GroupID === 'globals')
      if (footerBodies.length) inject(footerBodies)
    }
  }

  private createComponent(body: IBody, parentId?: string) {
    this.componentsMap.set(body._id, new ViewComponent(this, body, parentId))
    if (body._Body.length > 0) {
      body._Body.forEach((childBody) => {
        this.createComponent(childBody, body._id)
      })
    }
  }

  //--------------------------------------------------------------------------------------//
  //                             Subscriber Methods                                       //
  //--------------------------------------------------------------------------------------//

  public subscribe(callback: AppLayerSubscriber): () => void {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  notifySubscribers(): void {
    this.subscribers.forEach((callback) => callback())
    this.appClient.notifySubscribers(this.type) // also notify the app client
  }

  //--------------------------------------------------------------------------------------//
  //                              Common Layer Methods                                    //
  //--------------------------------------------------------------------------------------//

  /**
   * Update the current layer
   */
  async Update(layer?: Partial<ILayer>, rebuildComponents?: boolean, options?: UndoableOptions) {
    // Track changes in undo manager
    if (!options?.skipUndo) {
      const description = options?.description || this.getUpdateDescription(layer)
      const coalescingKey = createCoalescingKey(this.type, this.settings._id, 'update')

      this.appClient.undoManager.execute({
        id: generateUndoActionId(),
        timestamp: Date.now(),
        description,
        target: this.type,
        targetId: this.settings._id,
        before: structuredClone(this.settings),
        after: null, // Will be set after update
        canCoalesce: true,
        coalescingKey,
      })
    }

    const newSlug = layer?._Slug || ''
    const shouldUpdateSlug = newSlug.length > 0 && newSlug !== this.settings._Slug

    if (shouldUpdateSlug) {
      this.provider.updatePageSlug(this.appID, this.settings._id, newSlug)
    }

    if (layer) {
      this.settings = { ...this.settings, ...layer }
    }

    if (rebuildComponents) {
      this.buildComponentsMap()
    }

    // Update the 'after' state in the undo action
    if (!options?.skipUndo) {
      const lastAction = this.appClient.undoManager.getHistory().pop()
      if (lastAction) {
        lastAction.after = structuredClone(this.settings)
      }
    }

    this.notifySubscribers()

    // Skip network sync if requested (e.g., during undo/redo)
    if (!options?.skipNetwork) {
      updateWithTimeout(() => {
        if (this.type === 'page') {
          this.provider.updatePage(this.appID, this.settings as IPage).then(() => {
            this.appClient.RevalidateVersionHistory()
          })
        } else {
          this.provider.updateOverlay(this.appID, this.settings as IOverlay).then(() => {
            this.appClient.RevalidateVersionHistory()
          })
        }
      })
    }
  }

  /**
   * Get a user-friendly description of what changed
   */
  private getUpdateDescription(layer?: Partial<ILayer>): string {
    const layerWithName = layer as any
    const settingsWithName = this.settings as any

    if (layerWithName?._Name && layerWithName._Name !== settingsWithName._Name) {
      return `Renamed ${this.type} to "${layerWithName._Name}"`
    }
    return `Updated ${this.type}`
  }

  /**
   * Schedule network sync (can be called externally during undo/redo)
   */
  scheduleNetworkSync() {
    updateWithTimeout(() => {
      if (this.type === 'page') {
        this.provider.updatePage(this.appID, this.settings as IPage).then(() => {
          this.appClient.RevalidateVersionHistory()
        })
      } else {
        this.provider.updateOverlay(this.appID, this.settings as IOverlay).then(() => {
          this.appClient.RevalidateVersionHistory()
        })
      }
    })
  }

  /**
   * Disable the current layer
   */
  async Disable(value: boolean) {
    this.settings._Enabled = value
    await this.Update()
  }

  // add component to the body recursively
  private addComponentRecursive = (
    allComponents: IBody[],
    newComponent: IBody,
    parentId?: string,
    position?: number
  ): IBody[] => {
    if (!parentId) {
      allComponents.splice(position ? position : allComponents.length, 0, newComponent)
      return allComponents
    } else {
      let result = []
      // find parent (deep)
      for (let component of allComponents) {
        let comp = component
        if (component._id === parentId) {
          comp._Body.splice(position ? position : comp._Body.length, 0, newComponent)
          result.push(comp)
        } else {
          if (component._Body?.length > 0) {
            comp._Body = this.addComponentRecursive(component._Body, newComponent, parentId, position)
          }
          result.push(comp)
        }
      }
      return result
    }
  }

  /**
   * Add component to the current layer
   */
  async AddComponent(
    component: IBody,
    flatSettings: Record<string, any>,
    parentId?: string,
    position?: number,
    options?: UndoableOptions
  ) {
    if (!options?.skipUndo) {
      const description = options?.description || `Added ${getComponentLabel(component)}`

      this.appClient.undoManager.execute({
        id: generateUndoActionId(),
        timestamp: Date.now(),
        description,
        target: this.type,
        targetId: this.settings._id,
        before: {
          _Body: structuredClone(this.settings._Body),
          _BodySettings: structuredClone(this.settings._BodySettings ?? {}),
        },
        after: null, // Will be set after adding
      })
    }

    this.settings._Body = this.addComponentRecursive(this.settings._Body, component, parentId, position)
    this.settings._BodySettings = { ...this.settings._BodySettings, ...flatSettings }

    if (!options?.skipUndo) {
      const lastAction = this.appClient.undoManager.getHistory().pop()
      if (lastAction) {
        lastAction.after = {
          _Body: structuredClone(this.settings._Body),
          _BodySettings: structuredClone(this.settings._BodySettings ?? {}),
        }
      }
    }

    window.prettyLog('[AppLayer] add-component', [this.settings])
    await this.Update(this.settings, true, { skipUndo: true, skipNetwork: options?.skipNetwork })
  }

  // update the component in the body recursively
  private updateNestedBody = (bodyArray: IBody[], updatedComponent: IBody): IBody[] => {
    return bodyArray.map((component) => {
      // If this is the component we want to update
      if (component._id === updatedComponent._id) {
        return updatedComponent
      }

      // If this component has children, recursively search and update
      if (component._Body && component._Body.length > 0) {
        return {
          ...component,
          _Body: this.updateNestedBody(component._Body, updatedComponent),
        }
      }

      // Return unchanged component if no match
      return component
    })
  }

  /**
   * Use this if we update both body (children) and settings
   */
  async UpdateComponent(
    component: IViewComponent,
    bodyUpdate?: {
      bodyArray?: IBody[]
      bodySettings?: IBodySettings
    },
    options?: UndoableOptions
  ) {
    // If component is part of project-level globals, update _Globals (builder) and exit
    const globals = (this.appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }
    const containsInBodies = (bodies: IBody[], targetId: string): boolean => {
      for (const b of bodies) {
        if (b._id === targetId) return true
        if (b._Body?.length && containsInBodies(b._Body, targetId)) return true
      }
      return false
    }
    if (containsInBodies(globals._Body || [], component._id)) {
      const replaceInBodies = (bodies: IBody[], updated: IBody): IBody[] => {
        return bodies.map((b) => {
          if (b._id === updated._id) return updated
          if (b._Body?.length) return { ...b, _Body: replaceInBodies(b._Body, updated) }
          return b
        })
      }

      const newGlobalsBody = replaceInBodies(globals._Body || [], component.toBody())
      const newGlobalsSettings = {
        ...(globals._BodySettings || {}),
        ...(bodyUpdate?.bodySettings || {}),
        [component._id]: component.settings,
      }

      await this.appClient.UpdateGlobals({ _Body: newGlobalsBody as any, _BodySettings: newGlobalsSettings })
      return
    }
    if (!options?.skipUndo) {
      const description = options?.description || `Updated ${getComponentLabel(component.toBody())}`
      const coalescingKey = createCoalescingKey(this.type, this.settings._id, `component:${component._id}`)

      this.appClient.undoManager.execute({
        id: generateUndoActionId(),
        timestamp: Date.now(),
        description,
        target: this.type,
        targetId: this.settings._id,
        before: {
          _Body: structuredClone(this.settings._Body),
          _BodySettings: structuredClone(this.settings._BodySettings ?? {}),
        },
        after: null,
        canCoalesce: true,
        coalescingKey,
      })
    }

    const newBodyArray = [...(bodyUpdate?.bodyArray ?? this.settings._Body)]
    const newBodySettings = { ...(bodyUpdate?.bodySettings ?? this.settings._BodySettings) }

    this.settings._Body = this.updateNestedBody(newBodyArray, component.toBody())
    this.settings._BodySettings = { ...newBodySettings, [component._id]: component.settings }

    if (!options?.skipUndo) {
      const lastAction = this.appClient.undoManager.getHistory().pop()
      if (lastAction) {
        lastAction.after = {
          _Body: structuredClone(this.settings._Body),
          _BodySettings: structuredClone(this.settings._BodySettings ?? {}),
        }
      }
    }

    await this.Update(this.settings, true, { skipUndo: true, skipNetwork: options?.skipNetwork })
  }

  /**
   * Use this if we update only settings
   */
  async UpdateComponentSettings(component: IViewComponent, options?: UndoableOptions) {
    if (!this.settings._BodySettings) return

    // If component is part of project-level globals, update _Globals (builder) settings and exit
    const globals = (this.appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }
    const containsInBodies = (bodies: IBody[], targetId: string): boolean => {
      for (const b of bodies) {
        if (b._id === targetId) return true
        if (b._Body?.length && containsInBodies(b._Body, targetId)) return true
      }
      return false
    }
    if (containsInBodies(globals._Body || [], component._id)) {
      const newGlobalsSettings = { ...(globals._BodySettings || {}), [component._id]: component.settings }
      await this.appClient.UpdateGlobals({ _Body: globals._Body || [], _BodySettings: newGlobalsSettings })
      return
    }

    if (!options?.skipUndo) {
      const description = options?.description || `Updated ${getComponentLabel(component.toBody())} settings`
      const coalescingKey = createCoalescingKey(this.type, this.settings._id, `settings:${component._id}`)

      this.appClient.undoManager.execute({
        id: generateUndoActionId(),
        timestamp: Date.now(),
        description,
        target: this.type,
        targetId: this.settings._id,
        before: {
          _BodySettings: structuredClone(this.settings._BodySettings ?? {}),
        },
        after: null,
        canCoalesce: true,
        coalescingKey,
      })
    }

    this.settings._BodySettings = { ...this.settings._BodySettings, [component._id]: component.settings }

    if (!options?.skipUndo) {
      const lastAction = this.appClient.undoManager.getHistory().pop()
      if (lastAction) {
        lastAction.after = {
          _BodySettings: structuredClone(this.settings._BodySettings ?? {}),
        }
      }
    }

    await this.Update(undefined, false, { skipUndo: true, skipNetwork: options?.skipNetwork })
  }

  // remove component and its settings recursively
  private removeComponentRecursive = (bodyArray: IBody[], id: string): IBody[] => {
    const resultArray: IBody[] = []

    for (let body of bodyArray) {
      if (body._id === id) {
        const removeRecursively = (comp: IBody) => {
          delete this.settings._BodySettings![comp._id]
          this.componentsMap.delete(comp._id)
          comp._Body.forEach(removeRecursively)
        }

        removeRecursively(body)
        continue
      }

      if (body._Body.length > 0) {
        body._Body = this.removeComponentRecursive(body._Body, id)
      }

      resultArray.push(body)
    }

    return resultArray
  }

  /**
   * Delete component from the current layer
   */
  async DeleteComponent(id: string, options?: UndoableOptions) {
    const component = this.componentsMap.get(id)
    if (!component) return

    if (!options?.skipUndo) {
      const description = options?.description || `Deleted ${getComponentLabel(component.toBody())}`

      this.appClient.undoManager.execute({
        id: generateUndoActionId(),
        timestamp: Date.now(),
        description,
        target: this.type,
        targetId: this.settings._id,
        before: {
          _Body: structuredClone(this.settings._Body),
          _BodySettings: structuredClone(this.settings._BodySettings),
          componentId: id,
        },
        after: null,
      })
    }

    this.settings._Body = this.removeComponentRecursive(this.settings._Body, id)

    if (!options?.skipUndo) {
      const lastAction = this.appClient.undoManager.getHistory().pop()
      if (lastAction) {
        lastAction.after = {
          _Body: structuredClone(this.settings._Body),
          _BodySettings: structuredClone(this.settings._BodySettings),
        }
      }
    }

    // window.prettyLog('[AppLayer] delete-component', [this.type, this.settings])
    await this.Update(this.settings, true, { skipUndo: true, skipNetwork: options?.skipNetwork })
  }
}
